import React from 'react';
import {
  Paper,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Fade
} from '@mui/material';
// MUI 7's Grid ignores item/xs/sm/md, so these layouts collapsed to content width
// (dashboard on phones, ST-20261007-0001 follow-up). GridLegacy is the pre-v7 grid.
import Grid from '@mui/material/GridLegacy';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MoodIcon from '@mui/icons-material/Mood';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import WidgetInfoTooltip from './WidgetInfoTooltip';
import { dashboardTheme } from '../styles/dashboardTheme';

/**
 * WeeklyProgress Component
 * Shows weekly progress metrics and streaks
 */
const WeeklyProgress = ({ progress = {} }) => {
  const {
    checkInsCount = 0,
    averageMood = 0,
    averageLabel = 'Mood',
    averageMax = 5,
    streak = 0,
    goalsProgress = 0,
    journalCount = 0
  } = progress;

  const metrics = [
    {
      label: averageLabel,
      value: averageMood,
      max: averageMax,
      format: (v) => v.toFixed(1),
      color: 'primary',
      gradient: averageLabel === 'SUDS' ? 'linear-gradient(135deg, #EF5350 0%, #F44336 100%)' : dashboardTheme.colors.primary.gradient,
      icon: <MoodIcon />,
      suffix: 'average'
    },
    {
      label: 'Goals',
      value: goalsProgress,
      max: 100,
      format: (v) => `${v}%`,
      color: 'success',
      gradient: dashboardTheme.colors.success.gradient,
      icon: <TrendingUpIcon />
    },
    {
      label: 'Check-ins',
      value: checkInsCount,
      max: 7,
      format: (v) => `${v}/7`,
      color: 'secondary',
      gradient: dashboardTheme.colors.secondary.gradient,
      icon: <CheckCircleIcon />,
      suffix: 'this week'
    },
    {
      label: 'Journal',
      value: journalCount,
      max: 7,
      format: (v) => `${v}`,
      color: 'info',
      gradient: dashboardTheme.colors.info.gradient,
      icon: <EditIcon />,
      suffix: 'entries'
    }
  ];

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: dashboardTheme.shadows.medium,
        transition: dashboardTheme.animations.transition,
        '&:hover': {
          boxShadow: dashboardTheme.shadows.large
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Your Progress This Week
          </Typography>
          <WidgetInfoTooltip id="weeklyProgress" />
        </Box>
        {streak > 0 && (
          <Fade in={true}>
            <Chip
              icon={<WhatshotIcon />}
              label={`${streak} day streak`}
              sx={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8787 100%)',
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
              size="small"
            />
          </Fade>
        )}
      </Box>

      <Grid container spacing={2.5}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} key={index}>
            <Fade in={true} timeout={500 + (index * 100)}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {metric.icon && (
                      <Box sx={{ 
                        background: metric.gradient,
                        borderRadius: '8px',
                        p: 0.5,
                        display: 'flex',
                        color: 'white'
                      }}>
                        {metric.icon}
                      </Box>
                    )}
                    <Typography variant="body2" fontWeight="600" color="#1a1a1a">
                      {metric.label}
                    </Typography>
                    {metric.suffix && (
                      <Typography variant="caption" color="text.secondary">
                        {metric.suffix}
                      </Typography>
                    )}
                  </Box>
                  <Typography 
                    variant="h6" 
                    fontWeight="bold"
                    sx={{
                      background: metric.gradient,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {metric.format(metric.value)}
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative' }}>
                  <LinearProgress
                    variant="determinate"
                    value={(metric.value / metric.max) * 100}
                    sx={{ 
                      height: 10, 
                      borderRadius: 5,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        background: metric.gradient,
                        transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                      }
                    }}
                  />
                  {metric.value === metric.max && (
                    <Box sx={{
                      position: 'absolute',
                      right: -8,
                      top: -8,
                      color: '#10b981',
                      animation: 'pulse 2s infinite'
                    }}>
                      ✨
                    </Box>
                  )}
                </Box>
              </Box>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {streak >= 7 && (
        <Fade in={true}>
          <Box 
            sx={{ 
              mt: 3, 
              p: 2, 
              background: 'linear-gradient(135deg, rgba(255,107,107,0.1) 0%, rgba(255,135,135,0.1) 100%)',
              borderRadius: '12px',
              textAlign: 'center',
              border: '1px solid rgba(255,107,107,0.2)'
            }}
          >
            <Typography variant="body2" sx={{ 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8787 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              🎉 Amazing! You're on a {streak} day streak! Keep it up!
            </Typography>
          </Box>
        </Fade>
      )}
    </Paper>
  );
};

export default WeeklyProgress;